import { Button, PetitionUpdateCard, Spinner, Text } from '@change/ds-core-ui';

import { parseHtml } from '@change-corgi/core-react/html';
import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';

import { useLazyLoadedListFocus } from 'src/app/shared/hooks/focus';

import { usePetitionUpdates } from './hooks/usePetitionUpdates';

export const PetitionUpdates: React.FC = () => {
	const {
		data: { displayed, petitionUpdates, hasNextPage, nextPageLoading },
		actions: { loadMore },
	} = usePetitionUpdates();

	const { containerRef, loadingRef, loadMoreRef, getListItemRef, recordListEvent, lastEvent } =
		useLazyLoadedListFocus();

	const { translate } = useI18n();

	if (!displayed) return null;

	return (
		<div className="text-typography-strong flex flex-col gap-6" data-qa="featured-updates-container">
			<Text as="h2" variant="heading" size="small" data-qa="featured-updates-header">
				<Translate value="corgi.topic_page.petition_updates.heading" />
			</Text>

			<div ref={containerRef} className="flex flex-col items-center gap-6">
				<div className="grid w-full grid-cols-1 gap-6 md:grid-cols-3">
					{petitionUpdates.map((petitionUpdate, index) => (
						<div key={petitionUpdate.id} className="w-full">
							{lastEvent.type === 'loadMore' && index === lastEvent.firstNewItemIndex && (
								<div className="sr-only" role="alert">
									<TranslatePlural
										value="corgi.tag.petitions.fetched_more"
										count={petitionUpdates.length - lastEvent.previousItemsCount}
										replacements={{ count: petitionUpdates.length - lastEvent.previousItemsCount }}
									/>
								</div>
							)}
							<div ref={getListItemRef(index)} className="h-full">
								<PetitionUpdateCard
									data-qa="featured-updates-petition-card"
									className="h-full"
									description={parseHtml(petitionUpdate.description, { restrictedTags: [] })}
									href={`/p/${petitionUpdate.petition.slug}/u/${petitionUpdate.id}`}
									image={'photo' in petitionUpdate ? petitionUpdate.photo?.petitionMedium.url : null}
									imageAlt={petitionUpdate.title}
									metadata={petitionUpdate.createdAtLocalized}
									title={petitionUpdate.title}
									petitionTitle={petitionUpdate.petition.ask}
									readMoreTriggerText={translate('corgi.topic_page.petition_update_card.read_more')}
									closeModalText={translate('corgi.topic_page.petition_update_card.close_modal')}
								/>
							</div>
						</div>
					))}
				</div>

				{nextPageLoading && (
					<>
						<div className="sr-only" role="alert">
							<Translate value="corgi.topic_page.petition_updates.fetching_more" />
						</div>
						<Spinner className="mx-auto mt-6" ref={loadingRef} />
					</>
				)}

				{!nextPageLoading && hasNextPage && (
					<Button
						size="small"
						data-qa="featured-updates-show-more-button"
						className="max-md:w-full!"
						variant="secondary"
						onClick={() => {
							recordListEvent({ type: 'loadMore', itemsCount: petitionUpdates.length });
							loadMore();
						}}
						ref={loadMoreRef}
					>
						<Translate value="corgi.topic_page.petition_updates.show_more" />
					</Button>
				)}
			</div>
		</div>
	);
};
